import { useState, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Filter } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { format, subDays } from 'date-fns';
import { parsePhDate, todayInPH } from '@/lib/phDate';

interface Harvest {
  id: string;
  farm_id: string;
  cycle_id: string;
  harvest_date: string;
  harvest_type: 'partial' | 'final';
  total_weight_kg: number | string;
  piece_count: number | null;
  avg_weight_g: number | string | null;
  price_per_kg: number | string | null;
  buyer: string | null;
  size_grade: string | null;
  total_revenue: number | string | null;
  notes: string | null;
  pond_name?: string;
}

interface Cycle {
  id: string;
  pond_id: string;
  cycle_number: number;
}

type DateRange = '30d' | '90d' | 'year' | 'all';
type HarvestTypeFilter = 'all' | 'partial' | 'final';

interface HarvestsSearchParams {
  pond?: string;
  type?: HarvestTypeFilter;
  range?: DateRange;
}

export const Route = createFileRoute('/_app/harvests')({
  validateSearch: (search: Record<string, unknown>): HarvestsSearchParams => ({
    pond: typeof search.pond === 'string' ? search.pond : undefined,
    type: ['partial', 'final'].includes(search.type as string)
      ? (search.type as HarvestTypeFilter)
      : undefined,
    range: ['30d', '90d', 'year'].includes(search.range as string)
      ? (search.range as DateRange)
      : undefined,
  }),
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <HarvestsPage />
    </RoleGuard>
  ),
});

function toNum(val: number | string | null | undefined): number | null {
  if (val === null || val === undefined) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? null : n;
}

function HarvestsPage() {
  const { membership } = useMembership();
  const { pond: filterPondId, type: filterType, range: filterRange } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [sortField, setSortField] = useState<string>('harvest_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const dateRange = filterRange || 'all';
  const harvestType = filterType || 'all';

  // Fetch ponds for filter
  const { data: pondsData } = usePonds(membership?.farmId);

  // Fetch cycles for pond name resolution
  const { data: cyclesData } = useQuery({
    queryKey: ['cycles', membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: Cycle[] }>(
        `/api/v1/farms/${membership?.farmId}/cycles`
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });

  // Build lookups
  const pondNames = (pondsData || []).reduce((acc, p) => {
    acc[p.id] = p.name;
    return acc;
  }, {} as Record<string, string>);

  const cyclePondMap = (cyclesData || []).reduce((acc, c) => {
    acc[c.id] = { pond_id: c.pond_id, cycle_number: c.cycle_number };
    return acc;
  }, {} as Record<string, { pond_id: string; cycle_number: number }>);

  // Fetch harvests
  const { data: harvestsData, isLoading } = useQuery({
    queryKey: ['harvests', membership?.farmId],
    queryFn: async () => {
      const params: Record<string, string> = { limit: '100' };
      const response = await api.get<{ data: Harvest[] }>(
        `/api/v1/farms/${membership?.farmId}/harvests`,
        { params }
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });

  // Enrich and filter harvests
  const allHarvests = (harvestsData || []).map(h => {
    const cycleInfo = cyclePondMap[h.cycle_id];
    const pondId = cycleInfo?.pond_id;
    return {
      ...h,
      pond_name: pondId ? (pondNames[pondId] || 'Unknown') : 'Unknown',
      pond_id: pondId,
      cycle_number: cycleInfo?.cycle_number,
    };
  });

  // Apply filters
  let filtered = allHarvests;

  if (filterPondId) {
    filtered = filtered.filter(h => h.pond_id === filterPondId);
  }

  if (harvestType !== 'all') {
    filtered = filtered.filter(h => h.harvest_type === harvestType);
  }

  // Issue #131: use a stable PH-today anchor; previously `new Date()` per
  // render caused the cutoff to drift across midnight.
  const cutoffDate = useMemo(() => {
    if (dateRange === 'all') return null;
    const days = dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
    return subDays(parsePhDate(todayInPH()), days);
  }, [dateRange]);
  if (cutoffDate) {
    filtered = filtered.filter((h) => new Date(h.harvest_date) >= cutoffDate);
  }

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let valA: number | string = 0;
    let valB: number | string = 0;

    if (sortField === 'harvest_date') {
      valA = a.harvest_date;
      valB = b.harvest_date;
    } else if (sortField === 'total_weight_kg') {
      valA = toNum(a.total_weight_kg) ?? 0;
      valB = toNum(b.total_weight_kg) ?? 0;
    } else if (sortField === 'price_per_kg') {
      valA = toNum(a.price_per_kg) ?? 0;
      valB = toNum(b.price_per_kg) ?? 0;
    } else if (sortField === 'total_revenue') {
      valA = toNum(a.total_revenue) ?? 0;
      valB = toNum(b.total_revenue) ?? 0;
    } else if (sortField === 'pond_name') {
      valA = a.pond_name || '';
      valB = b.pond_name || '';
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Summary cards
  const totalWeight = filtered.reduce((s, h) => s + (toNum(h.total_weight_kg) ?? 0), 0);
  const totalRevenue = filtered.reduce((s, h) => s + (toNum(h.total_revenue) ?? 0), 0);
  // Only harvests that have a price count: unpriced kg would drag the average down.
  const pricedWeight = filtered.reduce(
    (s, h) => ((toNum(h.total_revenue) ?? 0) > 0 ? s + (toNum(h.total_weight_kg) ?? 0) : s),
    0
  );
  const avgPrice = pricedWeight > 0 ? totalRevenue / pricedWeight : 0;

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Harvests</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track harvest performance and revenue
          </p>
        </div>
        <Badge variant="neutral" size="md">
          {filtered.length} {filtered.length === 1 ? 'record' : 'records'}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard label="Total Weight" value={`${totalWeight.toFixed(0)} kg`} />
        <SummaryCard label="Total Revenue" value={totalRevenue > 0 ? `₱${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'} />
        <SummaryCard label="Harvest Count" value={String(filtered.length)} />
        <SummaryCard label="Avg Price/kg" value={avgPrice > 0 ? `₱${avgPrice.toFixed(0)}` : '—'} />
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />

        <select
          value={dateRange}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, range: e.target.value === 'all' ? undefined : e.target.value as DateRange }) })}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="year">This year</option>
          <option value="all">All time</option>
        </select>

        <select
          value={filterPondId || ''}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, pond: e.target.value || undefined }) })}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Ponds</option>
          {[...(pondsData || [])].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <select
          value={harvestType}
          onChange={(e) => navigate({ search: (prev) => ({ ...prev, type: e.target.value === 'all' ? undefined : e.target.value as HarvestTypeFilter }) })}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Types</option>
          <option value="partial">Partial</option>
          <option value="final">Final</option>
        </select>
      </div>

      {/* Harvests Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <SortHeader label="Date" field="harvest_date" current={sortField} dir={sortDir} onSort={toggleSort} />
                <SortHeader label="Pond" field="pond_name" current={sortField} dir={sortDir} onSort={toggleSort} />
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Cycle</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Type</th>
                <SortHeader label="Weight (kg)" field="total_weight_kg" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
                <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Pieces</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Grade</th>
                <SortHeader label="Price/kg" field="price_per_kg" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
                <SortHeader label="Revenue" field="total_revenue" current={sortField} dir={sortDir} onSort={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No harvest records found
                  </td>
                </tr>
              ) : (
                sorted.map((h) => {
                  const weight = toNum(h.total_weight_kg);
                  const price = toNum(h.price_per_kg);
                  const revenue = toNum(h.total_revenue);

                  return (
                    <tr key={h.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                        {format(new Date(h.harvest_date), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 px-4 text-gray-700 whitespace-nowrap">{h.pond_name}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {h.cycle_number ? `#${h.cycle_number}` : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={h.harvest_type === 'final' ? 'ok' : 'neutral'}
                          size="sm"
                        >
                          {h.harvest_type}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {weight?.toFixed(1) ?? '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-700">
                        {h.piece_count?.toLocaleString() ?? '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-700">{h.size_grade || '—'}</td>
                      <td className="py-3 px-4 text-right font-mono text-gray-700">
                        {price ? `₱${price.toFixed(0)}` : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-medium text-gray-900">
                        {revenue ? `₱${revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <Card.Body className="p-4 text-center">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </Card.Body>
    </Card>
  );
}

function SortHeader({
  label,
  field,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  field: string;
  current: string;
  dir: 'asc' | 'desc';
  onSort: (f: string) => void;
  align?: 'left' | 'right';
}) {
  const isActive = current === field;
  return (
    <th
      className={cn(
        'py-3 px-4 font-medium text-xs uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-gray-700 transition-colors',
        align === 'right' ? 'text-right' : 'text-left',
        isActive ? 'text-gray-900' : 'text-gray-500'
      )}
      onClick={() => onSort(field)}
    >
      {label}
      {isActive && (
        <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>
      )}
    </th>
  );
}
